import React, {
  forwardRef,
  useRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useEffect } from "react";
import styled from "styled-components";
import mergeRefs from "utils/mergeRefs";
import {
  space,
  layout,
  color,
  position,
  border,
  shadow,
  typography,
  variant,
} from "styled-system";

import { Flex, Box, Loader } from "atoms";
import MemoEyeHideIcon from "assets/icons/EyeHide";
import MemoEyeShowIcon from "assets/icons/EyeShow";
import MemoTickWithCircleIcon from "assets/icons/TickWithCircle";
import MemoCrossWithCircleIcon from "assets/icons/CrossWithCircle";

export const InputBase = styled.input`
  width: 100%;

  ${variant({
    variants: {
      primary: {
        p: "1.3rem 3.6rem 1.3rem 1.6rem",
        border: "1px solid",
        borderColor: "neutral.200",
        borderRadius: "1rem",
        color: "neutral.500",
        outline: "none",
        fontFamily: "primaryRegular",
        height: "4.8rem",
        fontSize: "1.6rem",
        "&:hover": {
          borderColor: "primary.900",
        },
        "&:focus, &:active": {
          borderColor: "primary.900",
          color: "neutral.800",
        },
        "&::placeholder": {
          color: "neutral.500",
        },
      },

      error: {
        display: "block",
        color: "error.900",
        border: "none",
        borderBottom: "1px solid",
        borderColor: "error.900",
      },
    },
  })};

  ${space}
  ${layout}
  ${color}
  ${position}
  ${border}
  ${shadow}
  ${typography}
`;
export const InputBaseTextArea = styled.textarea`
  width: 100%;

  ${variant({
    variants: {
      primary: {
        p: "1.3rem 3.6rem 1.3rem 1.6rem",
        border: "1px solid",
        borderColor: "neutral.200",
        borderRadius: "1rem",
        color: "neutral.500",
        outline: "none",
        fontFamily: "primaryRegular",
        height: "4.8rem",
        fontSize: "1.6rem",
        "&:hover": {
          borderColor: "primary.900",
        },
        "&:focus, &:active": {
          borderColor: "primary.900",
          color: "neutral.800",
        },
        "&::placeholder": {
          color: "neutral.500",
        },
      },

      error: {
        display: "block",
        color: "error.900",
        border: "none",
        borderBottom: "1px solid",
        borderColor: "error.900",
      },
    },
  })};

  ${space}
  ${layout}
  ${color}
  ${position}
  ${border}
  ${shadow}
  ${typography}
`;

export const Input = forwardRef(
  (
    {
      autoFocus,
      variant: v = "primary",
      type,
      onChange,
      status,
      isSearchable,
      onClose,
      loading,
      removeValidationIcon,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef(null);

    const [isOpen, setOpen] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [toggleVisible, setToggleVisible] = useState(false);

    const toggle = useCallback(() => setOpen((prev) => !prev), []);

    const isPassword = useMemo(() => type === "password", [type]);

    const onChangeHandler = useCallback(
      (e) => {
        if (typeof onChange === "function") {
          onChange(e);
        }
        setToggleVisible(e?.currentTarget?.value?.length > 0);
      },
      [onChange]
    );
    useEffect(() => {
      if (autoFocus) {
        inputRef?.current?.focus?.();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <>
        {loading && (
          <Flex
            left={0}
            right={0}
            position="absolute"
            justifyContent="center"
            alignItems="center"
            fontSize={2}
            height="15px"
            width="15px"
            mx="auto"
          >
            <Loader loading />
          </Flex>
        )}
        {
          <Flex
            position="absolute"
            right={0}
            cursor="pointer"
            alignItems="center"
            justifyContent="center"
            mr="1.7rem"
          >
            {!removeValidationIcon && status !== null && type !== "radio" && (
              <Box height="1.5rem">
                {status ? (
                  <MemoTickWithCircleIcon width="1.6rem" height="1.6rem" />
                ) : (
                  <Box color="accents.100">
                    <MemoCrossWithCircleIcon width="1.6rem" height="1.6rem" />
                  </Box>
                )}
              </Box>
            )}
            {isPassword && (
              <Box ml="1.5rem" height="1.4rem" onClick={toggle}>
                {isOpen ? (
                  <MemoEyeShowIcon width="1.6rem" height="1.4rem" />
                ) : (
                  <MemoEyeHideIcon width="1.6rem" height="1.4rem" />
                )}
              </Box>
            )}
            {!removeValidationIcon && isSearchable && (
              <Box
                color="secondary.500"
                ml="1.5rem"
                width="1.6rem"
                height="1.6rem"
                // position="absolute"
                // right="1.6rem"
                // top="1.7rem"
                cursor="pointer"
                onClick={onClose}
              >
                <MemoCrossWithCircleIcon width="1.6rem" height="1.6rem" />
              </Box>
            )}
          </Flex>
        }

        {type === "textarea" ? (
          <InputBaseTextArea
            type={isPassword && isOpen ? "text" : type}
            variant={v}
            ref={mergeRefs(ref, inputRef)}
            onChange={onChangeHandler}
            {...props}
            pr={isPassword ? "4.5rem" : undefined}
          />
        ) : (
          <InputBase
            type={isPassword && isOpen ? "text" : type}
            variant={v}
            ref={mergeRefs(ref, inputRef)}
            onChange={onChangeHandler}
            {...props}
            pr={isPassword ? "4.5rem" : undefined}
          />
        )}

        {/* )} */}
      </>
    );
  }
);
